/**
 * component-drag-carousel.js
 * Custom Element <edj-drag-carousel>
 * Lot 2 Batch D — EDJ Shopify theme
 *
 * Pixel-perfect ref : page-home-b.jsx HomeDesktopB lignes 218-230 + 380-386
 *   - drag horizontal au clic (mouse + touch + pointer)
 *   - momentum (inertie) après relâchement
 *   - scroll-snap mandatory sur l'axe X (CSS)
 *   - flèches clavier (← / →) déplacent d'environ une carte
 *   - boutons prev/next via slot ou data-attributes (data-edj-prev / data-edj-next)
 *   - prefers-reduced-motion : désactive l'inertie, conserve scroll natif
 *   - self-registering + idempotent (customElements.get check)
 *
 * HTML attendu :
 *   <edj-drag-carousel data-step="340">
 *     <div class="edj-carousel-track">…cards…</div>
 *   </edj-drag-carousel>
 *
 * Boutons externes (déclencheurs) :
 *   <button data-edj-prev="ID">←</button>
 *   <button data-edj-next="ID">→</button>
 *   où ID est le `data-section-id` du parent — voir best-sellers-carousel.liquid.
 */
(function () {
  'use strict';

  if (customElements.get('edj-drag-carousel')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class EdjDragCarousel extends HTMLElement {
    constructor() {
      super();
      this._track = null;
      this._isDown = false;
      this._startX = 0;
      this._startScroll = 0;
      this._lastX = 0;
      this._lastT = 0;
      this._velocity = 0;
      this._raf = null;
      this._pointerId = null;
      this._moved = false;
      // bind once
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onPointerCancel = this._onPointerCancel.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onPrevClick = this._onPrevClick.bind(this);
      this._onNextClick = this._onNextClick.bind(this);
    }

    connectedCallback() {
      // Find the scroll track : first child element by default, or [data-edj-track] within
      this._track = this.querySelector('[data-edj-track]') || this.firstElementChild;
      if (!this._track) return;

      // Make the track scrollable horizontally if not already
      const trackStyle = this._track.style;
      if (!trackStyle.overflowX) trackStyle.overflowX = 'auto';
      if (!trackStyle.cursor) trackStyle.cursor = 'grab';

      // Pointer events on the track
      this._track.addEventListener('pointerdown', this._onPointerDown);
      this._track.addEventListener('pointermove', this._onPointerMove);
      this._track.addEventListener('pointerup', this._onPointerUp);
      this._track.addEventListener('pointercancel', this._onPointerCancel);
      this._track.addEventListener('pointerleave', this._onPointerUp);
      // Suppress accidental click on children when dragging
      this._track.addEventListener('click', this._onClick, true);
      // Keyboard navigation (focusable track)
      if (!this._track.hasAttribute('tabindex')) {
        this._track.setAttribute('tabindex', '0');
      }
      this._track.setAttribute('role', 'region');
      if (!this._track.hasAttribute('aria-label')) {
        this._track.setAttribute('aria-label', 'Carrousel produits');
      }
      this._track.addEventListener('keydown', this._onKeyDown);

      // External prev/next buttons that target this carousel by id
      const carouselId = this.getAttribute('id') || this.dataset.carouselId;
      if (carouselId) {
        document.querySelectorAll(`[data-edj-prev="${carouselId}"]`).forEach(btn => {
          btn.addEventListener('click', this._onPrevClick);
        });
        document.querySelectorAll(`[data-edj-next="${carouselId}"]`).forEach(btn => {
          btn.addEventListener('click', this._onNextClick);
        });
      }
      // Slotted prev/next inside the host
      this.querySelectorAll('[data-edj-prev]:not([data-edj-prev*="="])').forEach(btn => {
        btn.addEventListener('click', this._onPrevClick);
      });
      this.querySelectorAll('[data-edj-next]:not([data-edj-next*="="])').forEach(btn => {
        btn.addEventListener('click', this._onNextClick);
      });
    }

    disconnectedCallback() {
      if (!this._track) return;
      this._track.removeEventListener('pointerdown', this._onPointerDown);
      this._track.removeEventListener('pointermove', this._onPointerMove);
      this._track.removeEventListener('pointerup', this._onPointerUp);
      this._track.removeEventListener('pointercancel', this._onPointerCancel);
      this._track.removeEventListener('pointerleave', this._onPointerUp);
      this._track.removeEventListener('click', this._onClick, true);
      this._track.removeEventListener('keydown', this._onKeyDown);
      cancelAnimationFrame(this._raf);
    }

    _step() {
      const explicit = parseInt(this.getAttribute('data-step') || '0', 10);
      if (explicit > 0) return explicit;
      // fallback : largeur de la première carte + gap
      const firstChild = this._track.querySelector(':scope > *');
      if (firstChild) {
        const rect = firstChild.getBoundingClientRect();
        return Math.round(rect.width + 18); // gap 18px par défaut (cf maquette)
      }
      return 340;
    }

    _onPrevClick(e) {
      e.preventDefault();
      this._scrollBy(-this._step());
    }

    _onNextClick(e) {
      e.preventDefault();
      this._scrollBy(this._step());
    }

    _scrollBy(delta) {
      if (!this._track) return;
      const behavior = REDUCED.matches ? 'auto' : 'smooth';
      this._track.scrollBy({ left: delta, behavior });
    }

    _onKeyDown(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._scrollBy(this._step());
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this._scrollBy(-this._step());
      } else if (e.key === 'Home') {
        e.preventDefault();
        this._track.scrollTo({ left: 0, behavior: REDUCED.matches ? 'auto' : 'smooth' });
      } else if (e.key === 'End') {
        e.preventDefault();
        this._track.scrollTo({
          left: this._track.scrollWidth,
          behavior: REDUCED.matches ? 'auto' : 'smooth'
        });
      }
    }

    _onPointerDown(e) {
      // Ignore right-click and middle-click
      if (e.button !== undefined && e.button !== 0) return;
      // Don't drag-hijack inputs / buttons / interactive children
      if (e.target.closest('button, input, select, textarea, a[data-no-drag]')) return;

      cancelAnimationFrame(this._raf);
      this._velocity = 0;
      this._isDown = true;
      this._moved = false;
      this._pointerId = e.pointerId;
      this._startX = e.clientX;
      this._lastX = e.clientX;
      this._startScroll = this._track.scrollLeft;
      this._lastT = performance.now();
      // Item 26 VAGUE 7 : NE PAS appeler setPointerCapture ici, ni modifier
      // cursor/scrollSnapType. Capturer le pointer sur _track redirige
      // pointerup/mouseup/click vers _track (DIV.edj-related__grid), ce qui fait
      // que le click n'atteint jamais le <a href> enfant : navigation cassée.
      // → On défère setPointerCapture / cursor=grabbing / scrollSnapType=none
      //   à _onPointerMove, UNIQUEMENT quand le seuil de drag (10px) est dépassé.
    }

    _onPointerMove(e) {
      if (!this._isDown) return;
      const dx = e.clientX - this._startX;
      // Drag threshold (item 26 VAGUE 3) : 10 px de déplacement avant de considérer
      // qu'on drag réellement. Sous ce seuil, on laisse le scrollLeft tranquille
      // et on ne marque PAS _moved → le click sur <a> enfant passera normalement.
      if (Math.abs(dx) <= 10) {
        // micro-mouvement involontaire : ne pas hijacker, ne pas scroller
        return;
      }
      // Drag réel détecté : on prend la capture du pointer maintenant pour suivre
      // le doigt même hors track, et on désactive scroll-snap pour fluidité.
      // Item 26 VAGUE 7 : déplacé depuis _onPointerDown (cf. commentaire bug click).
      if (!this._moved) {
        this._track.style.cursor = 'grabbing';
        this._track.style.scrollSnapType = 'none';
        try { this._track.setPointerCapture(this._pointerId); } catch (_) {}
      }
      this._moved = true;
      this._track.scrollLeft = this._startScroll - dx;

      // velocity for momentum
      const now = performance.now();
      const dt = now - this._lastT;
      if (dt > 0) {
        this._velocity = (e.clientX - this._lastX) / dt; // px / ms
      }
      this._lastX = e.clientX;
      this._lastT = now;
    }

    _onPointerUp(e) {
      if (!this._isDown) return;
      this._isDown = false;
      this._track.style.cursor = 'grab';
      try {
        if (this._pointerId !== null) this._track.releasePointerCapture(this._pointerId);
      } catch (_) {}
      this._pointerId = null;

      // Restore scroll-snap
      this._track.style.scrollSnapType = '';

      if (REDUCED.matches) return;
      // momentum
      if (Math.abs(this._velocity) > 0.05) {
        let v = -this._velocity * 16; // 1 frame ahead
        const decay = 0.94;
        const stopThreshold = 0.5;
        const tick = () => {
          this._track.scrollLeft += v;
          v *= decay;
          if (Math.abs(v) > stopThreshold) {
            this._raf = requestAnimationFrame(tick);
          }
        };
        this._raf = requestAnimationFrame(tick);
      }
    }

    _onPointerCancel() {
      this._isDown = false;
      this._track.style.cursor = 'grab';
      this._track.style.scrollSnapType = '';
      this._pointerId = null;
    }

    _onClick(e) {
      // suppress clicks fired right after a REAL drag movement (>10px)
      // Item 26 VAGUE 3 : si l'utilisateur a cliqué sur un <a href> sans drag réel,
      // on laisse passer le click même si _moved a été set par micro-jitter.
      const link = e.target.closest('a[href]');
      if (this._moved && !link) {
        e.preventDefault();
        e.stopPropagation();
      }
      // Si _moved est false → click normal, navigation autorisée
      // Si _moved est true mais link existe → on laisse passer (on a déjà filtré
      // par seuil 10px, c'est forcément un mouvement intentionnel mais l'utilisateur
      // a fini par cliquer sur un lien : on respecte sa dernière intention)
      this._moved = false;
    }
  }

  customElements.define('edj-drag-carousel', EdjDragCarousel);
})();
